import "./styles.css";

interface AnalyticsSummary {
  source: "analytics-engine" | "d1";
  approximate: boolean;
  days: number;
  totalClicks: number;
  metrics: Array<{ cta: string; clicks: number }>;
  paidAccesses: number;
}

interface StreamMediaRow {
  id: string;
  kind: "video" | "live";
  title: string;
  cloudflare_id: string;
  public_url: string;
  is_live: 0 | 1;
  live_enabled: 0 | 1;
  status: "pending" | "processing" | "ready" | "failed";
  created_at: string;
}

interface ImageRow {
  id: string;
  title: string;
  cloudflare_id: string;
  public_url: string;
  sha256: string;
  c2pa_status: "valid" | "present_unverified" | "invalid" | "not_applicable";
  c2pa_manifest_json: string;
  created_at: string;
}

interface ArticleRow {
  id: string;
  slug: string;
  title: string;
  dek: string;
  teaser_markdown: string;
  body_markdown: string;
  agent_summary_markdown: string | null;
  agent_summary_source: "author" | "workers_ai" | null;
  section: string;
  author: string;
  tier: "free" | "paid" | "premium";
  human_access: "open" | "premium";
  agent_search_price_cents: number;
  training_price_cents: number;
  status: "draft" | "published";
  hero_image_id: string | null;
  hero_image_url: string | null;
  hero_image_provenance: "c2pa" | "workers_ai" | null;
  video_uid: string | null;
  published_at: string;
  updated_at: string;
}

const app = requiredElement<HTMLElement>("#app");
const dialog = requiredElement<HTMLDialogElement>("#auth-dialog");
const authForm = requiredElement<HTMLFormElement>("#auth-form");
const connectionState = requiredElement<HTMLElement>("#connection-state");
const toast = requiredElement<HTMLElement>("#toast");
let token = sessionStorage.getItem("dispatch-admin-token") ?? "";
let articles: ArticleRow[] = [];
let images: ImageRow[] = [];

window.addEventListener("hashchange", () =>
  void render().catch((error) =>
    showToast(error instanceof Error ? error.message : "Navigation failed.", true),
  ),
);
authForm.addEventListener("submit", (event) => {
  event.preventDefault();
  const nextToken = new FormData(authForm).get("token")?.toString() ?? "";
  void unlock(nextToken);
});

if (token) {
  void unlock(token);
} else {
  dialog.showModal();
  renderLocked();
}

async function unlock(nextToken: string): Promise<void> {
  token = nextToken;
  try {
    await api("/api/health");
    sessionStorage.setItem("dispatch-admin-token", token);
    connectionState.textContent = "Connected";
    connectionState.classList.add("is-connected");
    dialog.close();
    await render();
  } catch {
    connectionState.textContent = "Locked";
    showToast("The admin token was rejected.", true);
    if (!dialog.open) dialog.showModal();
  }
}

async function render(): Promise<void> {
  const view = window.location.hash.slice(1) || "articles";
  document.querySelectorAll<HTMLAnchorElement>("[data-view]").forEach((link) => {
    link.classList.toggle("active", link.dataset.view === view);
  });
  if (view === "articles") await renderArticles();
  else if (view === "images") await renderImages();
  else if (view === "video") await renderVideo();
  else if (view === "live") await renderLive();
  else if (view === "analytics") await renderAnalytics(7);
  else renderPlaceholder(view);
}

async function renderArticles(): Promise<void> {
  app.innerHTML = '<section class="loading"><p>Loading newsroom…</p></section>';
  const [articleResponse, imageResponse] = await Promise.all([
    api<{ articles: ArticleRow[] }>("/api/articles"),
    api<{ images: ImageRow[] }>("/api/images"),
  ]);
  articles = articleResponse.articles;
  images = imageResponse.images;
  app.innerHTML = `
    <header class="page-heading">
      <div><p class="kicker">Publishing</p><h1>Articles</h1></div>
      <button class="secondary-button" id="setup-search">Set up AI Search</button>
    </header>
    <section class="metric-strip">
      <div><strong>${articles.filter((article) => article.status === "published").length}</strong><span>Published</span></div>
      <div><strong>${articles.filter((article) => article.human_access === "open").length}</strong><span>Open with ads</span></div>
      <div><strong>${articles.filter((article) => article.human_access === "premium").length}</strong><span>Subscription</span></div>
    </section>
    <section class="desk-grid">
      <div class="article-list">
        <div class="section-heading"><h2>Newsroom</h2><button id="new-article">New article</button></div>
        ${articles.map(articleListItem).join("")}
      </div>
      <div class="editor-panel" id="editor-panel">${articleForm()}</div>
    </section>
  `;
  requiredElement<HTMLButtonElement>("#setup-search").addEventListener("click", () =>
    void runAction(setupSearch),
  );
  requiredElement<HTMLButtonElement>("#new-article").addEventListener("click", () => showArticleForm());
  document.querySelectorAll<HTMLButtonElement>("[data-edit-slug]").forEach((button) => {
    button.addEventListener("click", () => showArticleForm(button.dataset.editSlug));
  });
  bindArticleForm();
}

function articleListItem(article: ArticleRow): string {
  return `
    <article class="article-row">
      <div>
        <span class="status status-${article.status}">${article.status}</span>
        <span class="tier">${article.human_access} · search $${(article.agent_search_price_cents / 100).toFixed(2)} · train $${(article.training_price_cents / 100).toFixed(2)}</span>
      </div>
      <h3>${escapeHtml(article.title)}</h3>
      <p>${escapeHtml(article.section)} · ${escapeHtml(article.author)}</p>
      <div class="article-actions"><button data-edit-slug="${escapeHtml(article.slug)}">Edit</button></div>
    </article>
  `;
}

function articleForm(article?: ArticleRow): string {
  return `
    <form id="article-form">
      <div class="section-heading"><h2>${article ? "Edit story" : "New story"}</h2><span>Markdown</span></div>
      <div class="field-pair">
        ${field("Title", "title", article?.title ?? "", "text", true)}
        ${field("Slug", "slug", article?.slug ?? "", "text", true)}
      </div>
      ${field("Dek", "dek", article?.dek ?? "", "textarea", true)}
      <div class="field-pair three">
        ${field("Section", "section", article?.section ?? "", "text", true)}
        ${field("Author", "author", article?.author ?? "", "text", true)}
        <label>Human access<select name="humanAccess"><option value="open" ${selected(article?.human_access, "open")}>Open · ads · agents $0.05 / $0.10</option><option value="premium" ${selected(article?.human_access, "premium")}>Premium · subscription · agents $0.15 / $0.50</option></select></label>
      </div>
      ${field("Search-safe teaser", "teaserMarkdown", article?.teaser_markdown ?? "", "textarea", true)}
      ${field("Full article", "bodyMarkdown", article?.body_markdown ?? "", "textarea-large", true)}
      ${field("Agent search summary · leave blank for Workers AI", "agentSummaryMarkdown", article?.agent_summary_markdown ?? "", "textarea", false)}
      <p class="field-note">Current source: ${article?.agent_summary_source ?? "generated when saved"}. This summary is delivered only after an agent-search payment.</p>
      <fieldset class="hero-source">
        <legend>Hero image</legend>
        <p>The author chooses the provenance path for this story.</p>
        <div class="source-options">
          <label><input type="radio" name="heroMode" value="existing" checked>Use existing image</label>
          <label><input type="radio" name="heroMode" value="upload">Upload C2PA JPEG</label>
          <label><input type="radio" name="heroMode" value="generate">Generate with Workers AI</label>
        </div>
        <div data-hero-panel="existing">
          <label>Existing image<select name="heroImageId"><option value="">No hero image</option>${images.map((image) => `<option value="${escapeHtml(image.id)}" ${selected(article?.hero_image_id ?? undefined, image.id)}>${escapeHtml(image.title)} · ${imageSourceLabel(image)}</option>`).join("")}</select></label>
        </div>
        <div data-hero-panel="upload" hidden>
          <label>C2PA JPEG<input name="heroUpload" type="file" accept="image/jpeg,.jpg,.jpeg"></label>
          <div id="article-c2pa-inspection" class="inspection-state">The original credentialed JPEG will be validated and preserved byte-for-byte in R2.</div>
        </div>
        <div data-hero-panel="generate" hidden>
          <div class="inspection-state">Workers AI will generate a Barcelona editorial image after the story is saved. It will be labeled as AI-generated, not as a C2PA photograph.</div>
        </div>
      </fieldset>
      ${field("Stream video UID", "videoUid", article?.video_uid ?? "", "text", false)}
      <label>Status<select name="status"><option value="draft" ${selected(article?.status, "draft")}>Draft</option><option value="published" ${selected(article?.status, "published")}>Published</option></select></label>
      <button class="primary-button" type="submit">Save and index story</button>
    </form>
  `;
}

function field(
  label: string,
  name: string,
  value: string,
  type: "text" | "url" | "textarea" | "textarea-large",
  required: boolean,
): string {
  const attributes = `name="${name}" ${required ? "required" : ""}`;
  const control = type.startsWith("textarea")
    ? `<textarea ${attributes} class="${type === "textarea-large" ? "large" : ""}">${escapeHtml(value)}</textarea>`
    : `<input ${attributes} type="${type}" value="${escapeHtml(value)}">`;
  return `<label>${label}${control}</label>`;
}

function showArticleForm(slug?: string): void {
  const article = articles.find((candidate) => candidate.slug === slug);
  requiredElement<HTMLElement>("#editor-panel").innerHTML = articleForm(article);
  bindArticleForm();
}

function bindArticleForm(): void {
  const form = requiredElement<HTMLFormElement>("#article-form");
  const updatePanels = () => {
    const mode = new FormData(form).get("heroMode")?.toString() ?? "existing";
    form.querySelectorAll<HTMLElement>("[data-hero-panel]").forEach((panel) => {
      panel.hidden = panel.dataset.heroPanel !== mode;
    });
  };
  form.querySelectorAll<HTMLInputElement>('input[name="heroMode"]').forEach((radio) => {
    radio.addEventListener("change", updatePanels);
  });
  updatePanels();
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void runAction(() => saveArticle(event.currentTarget as HTMLFormElement));
  });
}

async function saveArticle(form: HTMLFormElement): Promise<void> {
  const data = new FormData(form);
  const heroMode = value(data, "heroMode");
  let heroImageId = heroMode === "existing" ? optionalValue(data, "heroImageId") : null;
  if (heroMode === "upload") {
    const file = data.get("heroUpload");
    if (!(file instanceof File) || file.size === 0) {
      throw new Error("Choose a C2PA JPEG for the article.");
    }
    heroImageId = await uploadC2paAsset(
      file,
      value(data, "title"),
      requiredElement<HTMLElement>("#article-c2pa-inspection"),
    );
  }
  const payload = {
    title: value(data, "title"),
    slug: value(data, "slug"),
    dek: value(data, "dek"),
    section: value(data, "section"),
    author: value(data, "author"),
    humanAccess: value(data, "humanAccess"),
    status: value(data, "status"),
    teaserMarkdown: value(data, "teaserMarkdown"),
    bodyMarkdown: value(data, "bodyMarkdown"),
    agentSummaryMarkdown: optionalValue(data, "agentSummaryMarkdown") ?? "",
    heroImageId,
    videoUid: optionalValue(data, "videoUid"),
  };
  const result = await api<{ article: { id: string } }>("/api/articles", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (heroMode === "generate") {
    await generateArticleImage(result.article.id, false);
  }
  showToast("Story saved. Image provenance attached and AI Search queued.");
  await renderArticles();
}

async function setupSearch(): Promise<void> {
  const result = await api<{ status: string; queued: number }>("/api/ai-search/setup", {
    method: "POST",
  });
  showToast(`AI Search instance ${result.status}; ${result.queued} articles queued.`);
}

async function generateArticleImage(
  articleId: string,
  refresh = true,
): Promise<void> {
  showToast("Generating an editorial image with Workers AI…");
  await api(`/api/articles/${encodeURIComponent(articleId)}/generate-image`, {
    method: "POST",
  });
  showToast("Workers AI image generated, archived, and attached to the article.");
  if (refresh) await renderArticles();
}

async function renderImages(): Promise<void> {
  app.innerHTML = '<section class="loading"><p>Loading image library…</p></section>';
  const { images } = await api<{ images: ImageRow[] }>("/api/images");
  app.innerHTML = `
    <header class="page-heading">
      <div><p class="kicker">Content authenticity</p><h1>Images</h1></div>
    </header>
    <section class="provenance-note">
      <strong>Canonical-first workflow</strong>
      <p>The desk validates an existing Content Credential, archives the exact JPEG and SHA-256 in R2, and serves those original bytes through the public Worker without transformations that could strip the manifest.</p>
    </section>
    <section class="media-upload-grid">
      <form id="image-form" class="upload-panel">
        <div class="section-heading"><h2>Upload credentialed JPEG</h2><span>10 MB max</span></div>
        <label>Title<input name="title" required maxlength="200"></label>
        <label>JPEG with C2PA<input name="image" type="file" accept="image/jpeg,.jpg,.jpeg" required></label>
        <div id="c2pa-inspection" class="inspection-state">The file will be checked locally with the official C2PA WebAssembly SDK before upload.</div>
        <button class="primary-button" type="submit">Validate and upload</button>
      </form>
      <div class="media-library">
        <div class="section-heading"><h2>Library</h2><span>${images.length} assets</span></div>
        <div class="image-grid">
          ${images.length ? images.map(imageCard).join("") : '<p class="muted">No images uploaded yet.</p>'}
        </div>
      </div>
    </section>
  `;
  requiredElement<HTMLFormElement>("#image-form").addEventListener("submit", (event) => {
    event.preventDefault();
    void runAction(() => uploadImage(event.currentTarget as HTMLFormElement));
  });
}

function imageSourceLabel(image: ImageRow): string {
  return image.c2pa_status === "not_applicable"
    ? "Workers AI"
    : `C2PA · ${escapeHtml(image.c2pa_status.replaceAll("_", " "))}`;
}

function imageCard(image: ImageRow): string {
  return `
    <article class="image-card">
      <img src="${escapeHtml(image.public_url)}" alt="" loading="lazy">
      <div>
        <span class="status ${image.c2pa_status === "valid" ? "status-published" : "status-draft"}">${image.c2pa_status === "not_applicable" ? "Workers AI generated" : escapeHtml(image.c2pa_status.replaceAll("_", " "))}</span>
        <h3>${escapeHtml(image.title)}</h3>
        <code title="Canonical SHA-256">${escapeHtml(image.sha256.slice(0, 16))}…</code>
      </div>
    </article>
  `;
}

async function uploadImage(form: HTMLFormElement): Promise<void> {
  const formData = new FormData(form);
  const file = formData.get("image");
  if (!(file instanceof File)) throw new Error("Choose a JPEG first.");
  await uploadC2paAsset(
    file,
    value(formData, "title"),
    requiredElement<HTMLElement>("#c2pa-inspection"),
  );
  showToast("Credentialed master archived in R2 and ready for attachment.");
  await renderImages();
}

async function uploadC2paAsset(
  file: File,
  title: string,
  inspectionElement: HTMLElement,
): Promise<string> {
  inspectionElement.textContent = "Loading C2PA verifier…";
  const { inspectC2pa } = await import("./c2pa");
  const inspection = await inspectC2pa(file);
  if (!inspection.present) {
    inspectionElement.textContent = "Rejected: this JPEG has no Content Credential.";
    throw new Error("Upload rejected: C2PA credential required.");
  }
  if (inspection.state === "Invalid") {
    inspectionElement.textContent = `Rejected: invalid Content Credential. ${inspection.errors.join(" ")}`;
    throw new Error("Upload rejected: C2PA validation failed.");
  }
  inspectionElement.textContent = `${inspection.state} credential · ${inspection.manifestCount} manifest(s) · ${inspection.activeManifest ?? "no active label"}`;
  const response = await fetch("/api/images", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "image/jpeg",
      "X-Filename": asciiHeader(file.name),
      "X-Media-Title": asciiHeader(title),
      "X-C2PA-Status": inspection.state === "Valid" || inspection.state === "Trusted" ? "valid" : "present_unverified",
      "X-C2PA-Manifest": asciiHeader(inspection.activeManifest ?? "unknown"),
    },
    body: file,
  });
  if (!response.ok) throw new Error(`${response.status}: ${await response.text()}`);
  const result = (await response.json()) as { image: { id: string } };
  return result.image.id;
}

function asciiHeader(value: string): string {
  return value.replace(/[^\x20-\x7E]/g, "?").slice(0, 300);
}

async function renderVideo(): Promise<void> {
  app.innerHTML = '<section class="loading"><p>Loading video library…</p></section>';
  const { media } = await api<{ media: StreamMediaRow[] }>("/api/stream-media");
  const videos = media.filter((item) => item.kind === "video");
  app.innerHTML = `
    <header class="page-heading"><div><p class="kicker">Cloudflare Stream</p><h1>Video</h1></div></header>
    <section class="provenance-note">
      <strong>Direct upload</strong>
      <p>The browser sends the video directly to a one-use Stream URL. The Worker never buffers the file. Stream can then generate captions through Workers AI.</p>
    </section>
    <section class="media-upload-grid">
      <form id="video-form" class="upload-panel">
        <div class="section-heading"><h2>Upload video</h2><span>Direct to Stream</span></div>
        <label>Title<input name="title" required maxlength="200"></label>
        <label>Maximum duration in seconds<input name="maxDurationSeconds" type="number" min="1" max="21600" value="600" required></label>
        <label>Video file<input name="video" type="file" accept="video/*" required></label>
        <div id="video-progress" class="inspection-state">Files under 200 MB use a basic direct upload. Use tus for larger files.</div>
        <button class="primary-button" type="submit">Create URL and upload</button>
      </form>
      <div class="media-library">
        <div class="section-heading"><h2>Video library</h2><span>${videos.length} assets</span></div>
        <div class="video-list">${videos.length ? videos.map(videoCard).join("") : '<p class="muted">No videos uploaded yet.</p>'}</div>
      </div>
    </section>
  `;
  requiredElement<HTMLFormElement>("#video-form").addEventListener("submit", (event) => {
    event.preventDefault();
    void runAction(() => uploadVideo(event.currentTarget as HTMLFormElement));
  });
  document.querySelectorAll<HTMLButtonElement>("[data-caption-id]").forEach((button) => {
    button.addEventListener("click", () =>
      void runAction(() => generateVideoCaption(button.dataset.captionId ?? "")),
    );
  });
  document.querySelectorAll<HTMLButtonElement>("[data-sync-video]").forEach((button) => {
    button.addEventListener("click", () =>
      void runAction(() => syncVideo(button.dataset.syncVideo ?? "")),
    );
  });
}

function videoCard(video: StreamMediaRow): string {
  return `
    <article class="video-card">
      <iframe src="${escapeHtml(video.public_url)}" title="${escapeHtml(video.title)}" loading="lazy" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>
      <div><span class="status status-${video.status === "ready" ? "published" : "draft"}">${escapeHtml(video.status)}</span><h3>${escapeHtml(video.title)}</h3><code>${escapeHtml(video.cloudflare_id)}</code><button data-sync-video="${escapeHtml(video.cloudflare_id)}">Check processing status</button>${video.status === "ready" ? `<button data-caption-id="${escapeHtml(video.cloudflare_id)}">Generate English captions</button>` : ""}</div>
    </article>
  `;
}

async function uploadVideo(form: HTMLFormElement): Promise<void> {
  const data = new FormData(form);
  const file = data.get("video");
  if (!(file instanceof File)) throw new Error("Choose a video first.");
  if (file.size >= 200 * 1024 * 1024) {
    throw new Error("This direct-upload path supports files smaller than 200 MB. Use tus for larger files.");
  }
  const progress = requiredElement<HTMLElement>("#video-progress");
  progress.textContent = "Creating a one-use Stream upload URL…";
  const result = await api<{ upload: { uploadUrl: string; streamId: string } }>(
    "/api/videos/direct-upload",
    {
    method: "POST",
    body: JSON.stringify({
      title: value(data, "title"),
      maxDurationSeconds: Number(value(data, "maxDurationSeconds")),
    }),
  });
  progress.textContent = "Uploading directly to Cloudflare Stream…";
  const upload = new FormData();
  upload.set("file", file);
  const response = await fetch(result.upload.uploadUrl, { method: "POST", body: upload });
  if (!response.ok) throw new Error(`Stream upload failed: ${response.status}`);
  const status = await api<{ status: string }>(
    `/api/videos/${encodeURIComponent(result.upload.streamId)}/sync`,
    { method: "POST" },
  );
  showToast(`Video uploaded. Stream status: ${status.status}.`);
  await renderVideo();
}

async function syncVideo(videoId: string): Promise<void> {
  const status = await api<{ status: string }>(
    `/api/videos/${encodeURIComponent(videoId)}/sync`,
    { method: "POST" },
  );
  showToast(`Stream status: ${status.status}.`);
  await renderVideo();
}

async function generateVideoCaption(videoId: string): Promise<void> {
  await api(`/api/videos/${encodeURIComponent(videoId)}/captions`, {
    method: "POST",
    body: JSON.stringify({ language: "en" }),
  });
  showToast("English caption generation started.");
}

async function renderLive(): Promise<void> {
  app.innerHTML = '<section class="loading"><p>Loading live inputs…</p></section>';
  const { media } = await api<{ media: StreamMediaRow[] }>("/api/stream-media");
  const liveInputs = media.filter((item) => item.kind === "live");
  app.innerHTML = `
    <header class="page-heading"><div><p class="kicker">Cloudflare Stream Live</p><h1>Live inputs</h1></div></header>
    <section class="provenance-note">
      <strong>Enable, then broadcast</strong>
      <p>Starting here enables an ingest endpoint. The broadcast begins only when OBS, a camera, or another encoder connects using the RTMPS or SRT credentials returned at creation.</p>
    </section>
    <section class="media-upload-grid">
      <form id="live-form" class="upload-panel">
        <div class="section-heading"><h2>Create live input</h2><span>Recording on</span></div>
        <label>Broadcast name<input name="name" required maxlength="120"></label>
        <button class="primary-button" type="submit">Create disabled input</button>
        <div id="live-credentials" class="credential-box" hidden></div>
      </form>
      <div class="media-library">
        <div class="section-heading"><h2>Inputs</h2><span>${liveInputs.length} configured</span></div>
        <div class="live-list">${liveInputs.length ? liveInputs.map(liveCard).join("") : '<p class="muted">No live inputs created yet.</p>'}</div>
      </div>
    </section>
  `;
  requiredElement<HTMLFormElement>("#live-form").addEventListener("submit", (event) => {
    event.preventDefault();
    void runAction(() => createLive(event.currentTarget as HTMLFormElement));
  });
  document.querySelectorAll<HTMLButtonElement>("[data-live-id]").forEach((button) => {
    button.addEventListener("click", () =>
      void runAction(() => setLive(button.dataset.liveId ?? "", button.dataset.enabled !== "true")),
    );
  });
}

function liveCard(input: StreamMediaRow): string {
  return `
    <article class="live-row">
      <span class="live-dot ${input.is_live ? "active" : ""}"></span>
      <div><h3>${escapeHtml(input.title)}</h3><span>${input.is_live ? "On air" : input.live_enabled ? "Ready for ingest" : "Disabled"}</span><code>${escapeHtml(input.cloudflare_id)}</code></div>
      <button data-live-id="${escapeHtml(input.cloudflare_id)}" data-enabled="${Boolean(input.live_enabled)}">${input.live_enabled ? "Stop accepting ingest" : "Enable ingest"}</button>
    </article>
  `;
}

async function createLive(form: HTMLFormElement): Promise<void> {
  const data = new FormData(form);
  const result = await api<{
    liveInput: {
      uid: string;
      rtmps?: { url: string; streamKey: string };
      srt?: { url: string; streamId: string; passphrase: string };
    };
  }>("/api/live-inputs", {
    method: "POST",
    body: JSON.stringify({ name: value(data, "name") }),
  });
  const credentials = requiredElement<HTMLElement>("#live-credentials");
  credentials.hidden = false;
  credentials.innerHTML = `
    <strong>Copy now—these credentials are not stored here.</strong>
    <code>RTMPS URL: ${escapeHtml(result.liveInput.rtmps?.url ?? "Unavailable")}</code>
    <code>Stream key: ${escapeHtml(result.liveInput.rtmps?.streamKey ?? "Unavailable")}</code>
    <code>SRT URL: ${escapeHtml(result.liveInput.srt?.url ?? "Unavailable")}</code>
    <code>SRT stream ID: ${escapeHtml(result.liveInput.srt?.streamId ?? "Unavailable")}</code>
    <code>SRT passphrase: ${escapeHtml(result.liveInput.srt?.passphrase ?? "Unavailable")}</code>
  `;
  showToast("Live input created in the disabled state.");
}

async function setLive(id: string, enabled: boolean): Promise<void> {
  await api(`/api/live-inputs/${encodeURIComponent(id)}/enabled`, {
    method: "POST",
    body: JSON.stringify({ enabled }),
  });
  showToast(enabled ? "Ingest enabled. Connect the encoder to go live." : "Ingest stopped.");
  await renderLive();
}

async function runAction(action: () => Promise<void>): Promise<void> {
  try {
    await action();
  } catch (error) {
    showToast(error instanceof Error ? error.message : "Action failed.", true);
  }
}

async function renderAnalytics(days: number): Promise<void> {
  app.innerHTML = '<section class="loading"><p>Querying Cloudflare analytics…</p></section>';
  const summary = await api<AnalyticsSummary>(`/api/analytics?days=${days}`);
  const max = Math.max(...summary.metrics.map((metric) => metric.clicks), 1);
  app.innerHTML = `
    <header class="page-heading">
      <div><p class="kicker">Cloudflare data</p><h1>Audience actions</h1></div>
      <div class="range-picker"><button data-days="1" class="${days === 1 ? "active" : ""}">24h</button><button data-days="7" class="${days === 7 ? "active" : ""}">7d</button><button data-days="30" class="${days === 30 ? "active" : ""}">30d</button></div>
    </header>
    <section class="analytics-source">
      <span class="source-pill">${summary.source === "analytics-engine" ? "Workers Analytics Engine" : "D1 local/exact fallback"}</span>
      <p>${summary.approximate ? "Analytics Engine uses adaptive sampling, so these are weighted estimates." : "These are exact rows stored in D1."}</p>
    </section>
    <section class="metric-strip analytics-metrics">
      <div><strong>${formatNumber(summary.totalClicks)}</strong><span>CTA clicks</span></div>
      <div><strong>${summary.metrics.length ? escapeHtml(humanizeCta(summary.metrics[0].cta)) : "—"}</strong><span>Most popular CTA</span></div>
      <div><strong>${formatNumber(summary.paidAccesses)}</strong><span>Agent license purchases</span></div>
    </section>
    <section class="analytics-panel">
      <div class="section-heading"><h2>CTA popularity</h2><span>Last ${summary.days} day${summary.days === 1 ? "" : "s"}</span></div>
      <div class="bar-chart">
        ${summary.metrics.length ? summary.metrics.map((metric) => analyticsBar(metric, max)).join("") : '<p class="muted">No tracked clicks in this period. Open the public site and use a Read more button.</p>'}
      </div>
    </section>
  `;
  document.querySelectorAll<HTMLButtonElement>("[data-days]").forEach((button) => {
    button.addEventListener("click", () =>
      void runAction(() => renderAnalytics(Number(button.dataset.days))),
    );
  });
}

function analyticsBar(metric: { cta: string; clicks: number }, max: number): string {
  const width = Math.max(2, (metric.clicks / max) * 100);
  return `<div class="bar-row"><span>${escapeHtml(humanizeCta(metric.cta))}</span><div><i style="width:${width}%"></i></div><strong>${formatNumber(metric.clicks)}</strong></div>`;
}

function humanizeCta(value: string): string {
  return value.replaceAll("-", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("en").format(value);
}

function renderPlaceholder(view: string): void {
  app.innerHTML = `<section class="empty-view"><p class="kicker">Rambla Oberta Desk</p><h1>${escapeHtml(titleCase(view))}</h1><p>This module is the next tested stage of the build.</p></section>`;
}

function renderLocked(): void {
  app.innerHTML = '<section class="empty-view"><p class="kicker">Protected</p><h1>Unlock the desk to continue.</h1></section>';
}

async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...init.headers,
    },
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`${response.status}: ${detail}`);
  }
  return response.json() as Promise<T>;
}

function selected(current: string | undefined, value: string): string {
  return current === value ? "selected" : "";
}

function value(data: FormData, key: string): string {
  return data.get(key)?.toString().trim() ?? "";
}

function optionalValue(data: FormData, key: string): string | null {
  return value(data, key) || null;
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function showToast(message: string, error = false): void {
  toast.textContent = message;
  toast.classList.toggle("is-error", error);
  toast.classList.add("visible");
  window.setTimeout(() => toast.classList.remove("visible"), 3500);
}

function escapeHtml(value: string): string {
  const element = document.createElement("span");
  element.textContent = value;
  return element.innerHTML;
}

function requiredElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing required element: ${selector}`);
  return element;
}
